<script setup>
/* eslint-disable no-console, no-use-before-define, no-plusplus, dot-notation, prettier/prettier, vue/multiline-html-element-content-newline, vue/attributes-order, vue/no-bare-strings-in-template, vue/prefer-separate-static-class, @intlify/vue-i18n/no-raw-text */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { toast } from 'dashboard/helper/toast';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import DialerPauseBar from './DialerPauseBar.vue';
import { useWhatsAppSipConnection } from 'dashboard/api/telephony_whatsapp_sip/useWhatsAppSipConnection';
import WhatsAppSipMyExtensionAPI from 'dashboard/api/whatsappSip/myExtension';
import ContactsAPI from 'dashboard/api/contacts';
import TransferNotificationsAPI from 'dashboard/api/whatsappSip/transferNotifications';
import { useWhatsAppCallPermission } from 'dashboard/composables/useWhatsAppCallPermission';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useCallHistory } from 'dashboard/api/telephony_aci/useCallHistory';

const props = defineProps({
  initialNumber: { type: String, default: '' },
  initialInboxId: { type: [Number, String], default: null },
  autoCall: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

// Layout constants
const PANEL_WIDTH = 280;
const PANEL_EXPANDED_HEIGHT = 530;
const PANEL_COMPACT_HEIGHT = 48;
const VIEWPORT_PADDING = 16;
const DEBOUNCE_DELAY = 300;
const DOUBLE_CLICK_GUARD_MS = 500;
const SHORT_GUARD_MS = 300;
const POST_CALL_COOLDOWN_MS = 1000;

// SIP Connection
const {
  isConnected,
  isConnecting,
  connectionError,
  callState,
  callDirection,
  callNumber,
  remoteDisplayName,
  isMuted,
  isOnHold,
  hasActiveCall,
  isInCall,
  isConsultationActive,
  consultationState,
  consultationTarget,
  connect,
  disconnect,
  makeCall: sipMakeCall,
  answerCall: sipAnswerCall,
  hangup,
  toggleMute,
  toggleHold: sipToggleHold,
  transfer: sipTransfer,
  attendedTransfer: sipAttendedTransfer,
  completeAttendedTransfer: sipCompleteAttendedTransfer,
  cancelAttendedTransfer: sipCancelAttendedTransfer,
  sendDTMF,
  getCallDuration,
  formatDuration,
  unlockAudio,
} = useWhatsAppSipConnection();

const {
  callHistory,
  addToHistory,
  clearHistory: clearCallHistory,
  formatTimestamp: formatHistoryTime,
  formatDuration: formatHistoryDuration,
} = useCallHistory();

// Unlock audio on first user interaction (required by browsers)
const handleUserInteraction = () => {
  unlockAudio();
};

// SIP on/off toggle
const isTogglingConnection = ref(false);

const toggleSipConnection = async () => {
  if (isTogglingConnection.value || hasActiveCall.value) return;
  isTogglingConnection.value = true;
  try {
    if (isConnected.value || isConnecting.value) {
      disconnect();
    } else {
      const { data } = await WhatsAppSipMyExtensionAPI.get();
      if (data.success && data.credentials) {
        await connect({
          extension: data.credentials.extension,
          password: data.credentials.password,
          displayName: data.credentials.display_name,
          sipServer: data.credentials.sip_server,
          wsServer: data.credentials.ws_server,
        });
      }
    }
  } finally {
    setTimeout(() => {
      isTogglingConnection.value = false;
    }, SHORT_GUARD_MS);
  }
};

// Computed for incoming call
const isIncomingCall = computed(() => {
  return callState.value === 'ringing' && callDirection.value === 'incoming';
});

// Agents from store (for extension lookup)
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Outbound trunks (WhatsApp numbers the agent can dial from)
const trunks = computed(() => store.getters['whatsappSipTrunks/getRecords'] || []);
const hasMultipleTrunks = computed(() => trunks.value.length >= 2);
const selectedInboxId = ref(null);
const isTrunkMenuOpen = ref(false);

const selectedTrunk = computed(() =>
  trunks.value.find(trunk => trunk.inbox_id === selectedInboxId.value) || null
);

// Resolve the inbox object for a trunk so we can show its channel icon (the
// WhatsApp badge) next to the number, like the conversation list does.
const inboxFor = trunk =>
  (trunk ? store.getters['inboxes/getInbox'](trunk.inbox_id) : null) || {};

// Initialize the selected trunk once trunks load. Prefer the inbox the call was
// launched from (props.initialInboxId) when a matching trunk exists; otherwise
// fall back to the 1st trunk. Runs before auto-call dials (auto-call awaits
// trunks/connection via canCall), so selectedInboxId reflects the right inbox.
watch(
  trunks,
  list => {
    if (selectedInboxId.value || list.length === 0) return;

    if (props.initialInboxId !== null && props.initialInboxId !== '') {
      const wanted = Number(props.initialInboxId);
      const match = list.find(trunk => Number(trunk.inbox_id) === wanted);
      if (match) {
        selectedInboxId.value = match.inbox_id;
        return;
      }
    }

    selectedInboxId.value = list[0].inbox_id;
  },
  { immediate: true }
);

const selectTrunk = inboxId => {
  selectedInboxId.value = inboxId;
  isTrunkMenuOpen.value = false;
};

// Current user
const currentUser = computed(() => store.getters['getCurrentUser'] || {});

// Get current user's WhatsApp SIP extension
const currentUserExtension = computed(() => {
  const user = currentUser.value;
  const ext = user?.whatsapp_sip_extension;
  return ext ? String(ext) : null;
});

// Check if number is a known WhatsApp SIP extension
const isExtensionNumber = (number) => {
  if (!number) return false;
  const clean = String(number).replace(/\D/g, '');
  return agents.value.some(a => String(a.whatsapp_sip_extension) === clean);
};

// Ramal interno (SIP) é chamada direta — NÃO passa pela permissão de chamada do
// WhatsApp, que resolve o destino para E.164 e rejeita ramal como "número
// inválido ou incompleto". Mesma regra de ramal do AciSipClient (número curto,
// até 5 dígitos); cobre também ramal cadastrado de agente.
const isInternalExtension = (number) => {
  const clean = String(number ?? '').replace(/\D/g, '');
  if (!clean) return false;
  return clean.length <= 5 || isExtensionNumber(clean);
};

// Find agent by WhatsApp SIP extension number
const findAgentByExtension = (extensionNumber) => {
  const clean = String(extensionNumber).replace(/\D/g, '');
  return agents.value.find(agent => {
    const agentExt = agent.whatsapp_sip_extension;
    return agentExt && String(agentExt) === clean;
  });
};

// Caller lookup
const callerContact = ref(null);
const callerAgent = ref(null);
const isLoadingCaller = ref(false);

// Flag: incoming call was originally a transfer consultation → can now re-transfer
const isTransferReceived = ref(false);

let lookupRequestId = 0;
async function lookupCaller(phoneNum) {
  if (!phoneNum) {
    callerContact.value = null;
    callerAgent.value = null;
    return;
  }

  const requestId = ++lookupRequestId;
  isLoadingCaller.value = true;

  try {
    if (isExtensionNumber(phoneNum)) {
      const agent = findAgentByExtension(phoneNum);
      if (requestId !== lookupRequestId) return;
      callerAgent.value = agent || null;
      callerContact.value = null;
      return;
    }

    if (phoneNum.length < 3) {
      callerContact.value = null;
      callerAgent.value = null;
      return;
    }

    const response = await ContactsAPI.lookupByPhone(phoneNum);
    if (requestId !== lookupRequestId) return;

    if (response.data?.found && response.data?.payload) {
      callerContact.value = response.data.payload;
      callerAgent.value = null;
    } else {
      callerContact.value = null;
      callerAgent.value = null;
    }
  } catch (error) {
    if (requestId !== lookupRequestId) return;
    callerContact.value = null;
    callerAgent.value = null;
  } finally {
    if (requestId === lookupRequestId) {
      isLoadingCaller.value = false;
    }
  }
}

watch(
  [callNumber, callDirection],
  ([newNumber, newDirection]) => {
    if (newNumber && newDirection) {
      lookupCaller(newNumber);
    } else if (!newNumber) {
      callerContact.value = null;
      callerAgent.value = null;
    }
  },
  { immediate: true }
);

const callerDisplayName = computed(() => {
  return callerAgent.value?.name
    || callerContact.value?.name
    || remoteDisplayName.value
    || callNumber.value
    || t('WHATSAPP_SIP_DIALER.INCOMING.UNKNOWN');
});

const callerThumbnail = computed(() => {
  if (callerAgent.value) return callerAgent.value.thumbnail || callerAgent.value.avatar_url;
  if (callerContact.value) return callerContact.value.thumbnail;
  return null;
});

const canShowTransferButton = computed(() => {
  if (callDirection.value === 'outgoing') return true;
  if (callDirection.value === 'incoming') {
    if (callerAgent.value || isExtensionNumber(callNumber.value)) {
      return isTransferReceived.value;
    }
    return true;
  }
  return true;
});

// ============================================
// Tabs
// ============================================
const activeTab = ref('dialpad');
const allTabs = [
  { id: 'dialpad', icon: 'i-lucide-phone', label: 'Telefone', activeColor: 'text-green-600 dark:text-green-400' },
  { id: 'extensions', icon: 'i-lucide-headphones', label: 'Ramais', activeColor: 'text-n-iris-11' },
  { id: 'history', icon: 'i-lucide-history', label: 'Histórico', activeColor: 'text-n-amber-11' },
  { id: 'contacts', icon: 'i-lucide-users', label: 'Contatos', activeColor: 'text-n-blue-11' },
];
const tabs = computed(() =>
  hasActiveCall.value
    ? allTabs.filter(tab => tab.id !== 'extensions')
    : allTabs
);

// Handle call state changes: always show dialpad and auto-expand on active call
watch(hasActiveCall, (active) => {
  if (active) {
    activeTab.value = 'dialpad';
    if (isCompact.value) isCompact.value = false;
  }
});

// Auto-close transfer modal when consultation ends unexpectedly
watch(isConsultationActive, (active, wasActive) => {
  if (!active && wasActive && showTransferModal.value && transferMode.value === 'attended') {
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';
  }
});

// ============================================
// WhatsApp SIP Extensions Tab
// ============================================
const extensionsList = computed(
  () => store.getters['whatsappSipExtensions/getRecords'] || []
);
const isLoadingExtensions = computed(
  () => store.getters['whatsappSipExtensions/getUIFlags']?.isFetching || false
);
const extensionSearch = ref('');

// Presença de ramal é INFORMATIVA, nunca gate de discagem: o ramal segue
// discável sempre que o dialer está conectado (se o destino não estiver
// registrado, a ligação falha visível). Fonte: contact_status em lote (cache
// AMI ContactStatus do Go, ver .ai/dialer-ramal-presenca-pendente.md) +
// updates realtime via ActionCable `whatsapp_sip.extension_presence`.
const extensionPresenceDot = ext => {
  switch (ext.contact_status) {
    case 'reachable':
      return { dotClass: 'bg-n-teal-9', label: 'Telefone conectado' };
    case 'unreachable':
      return { dotClass: 'bg-n-amber-9', label: 'Telefone sem responder' };
    case 'offline':
      return { dotClass: 'bg-n-slate-8', label: 'Telefone desconectado' };
    default:
      // unknown/ausente: não inventa estado — sem dot
      return null;
  }
};

const extensions = computed(() => {
  const myExtension = currentUserExtension.value;
  const query = extensionSearch.value.toLowerCase().trim();

  const filtered = extensionsList.value.filter(ext => {
    if (!ext.enabled) return false;
    if (myExtension && String(ext.extension_number) === myExtension) return false;
    if (query) {
      const name = (ext.display_name || '').toLowerCase();
      const number = String(ext.extension_number);
      if (!name.includes(query) && !number.includes(query)) return false;
    }
    return true;
  });

  return [...filtered].sort((a, b) => {
    const nameA = (a.display_name || '').toLowerCase();
    const nameB = (b.display_name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

const loadExtensions = () => {
  return store.dispatch('whatsappSipExtensions/fetch');
};

const callExtension = async (ext) => {
  if (!isConnected.value || isCallCooldown.value) return;
  phoneNumber.value = String(ext.extension_number);
  selectedContact.value = null;
  activeTab.value = 'dialpad';
  await nextTick();
  if (!hasActiveCall.value && canCall.value) {
    handleMakeCall();
  }
};

// ============================================
// Contacts Tab
// ============================================
const searchQuery = ref('');
const selectedContact = ref(null);
const contactsListRef = ref(null);

const contacts = computed(() => store.getters['contacts/getContactsList'] || []);
const contactsUIFlags = computed(() => store.getters['contacts/getUIFlags'] || {});
const contactsMeta = computed(() => store.getters['contacts/getMeta'] || {});
const isSearching = computed(() => contactsUIFlags.value.isFetching);
const hasMoreContacts = computed(() => {
  const meta = contactsMeta.value;
  return meta?.currentPage < meta?.totalPages;
});

async function loadInitialContacts() {
  try {
    await store.dispatch('contacts/get', { page: 1, sortAttr: '-last_activity_at' });
  } catch (error) {
    console.error('[WhatsAppSipDialerPanel] Error loading contacts:', error);
  }
}

async function loadMoreContacts() {
  if (isSearching.value || !hasMoreContacts.value || searchQuery.value) return;
  const nextPage = (contactsMeta.value?.currentPage || 0) + 1;
  try {
    await store.dispatch('contacts/get', { page: nextPage, sortAttr: '-last_activity_at' });
  } catch (error) {
    console.error('[WhatsAppSipDialerPanel] Error loading more contacts:', error);
  }
}

function handleContactsScroll(event) {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;
  if (nearBottom && !searchQuery.value) loadMoreContacts();
}

let searchTimeout = null;
const debouncedSearch = query => {
  clearTimeout(searchTimeout);
  if (!query || query.length < 2) {
    if (!query) loadInitialContacts();
    return;
  }
  searchTimeout = setTimeout(async () => {
    try {
      await store.dispatch('contacts/search', { search: query, page: 1, sortAttr: '-last_activity_at' });
    } catch (error) {
      console.error('[WhatsAppSipDialerPanel] Error searching contacts:', error);
    }
  }, DEBOUNCE_DELAY);
};

watch(searchQuery, newValue => debouncedSearch(newValue));

const selectContact = async contact => {
  const contactPhone = contact.phoneNumber || contact.phone_number || '';
  if (!contactPhone) {
    toast.error(t('WHATSAPP_SIP_DIALER.TOAST.NO_PHONE'));
    return;
  }
  selectedContact.value = contact;
  phoneNumber.value = contactPhone;
  searchQuery.value = '';
  activeTab.value = 'dialpad';
  await nextTick();
  if (isConnected.value && !hasActiveCall.value && canCall.value) {
    handleMakeCall();
  }
};

const getContactPhone = contact => {
  return contact.phoneNumber || contact.phone_number || contact.additionalAttributes?.phone || contact.additional_attributes?.phone || '-';
};

// ============================================
// Panel Controls: Drag, Compact
// ============================================
const panelRef = ref(null);
const isCompact = ref(false);
const isDragging = ref(false);

// Position (null = use default CSS position)
const position = ref({ x: null, y: null });

const loadPanelPreferences = () => {
  try {
    const saved = localStorage.getItem('wa_sip_dialer_panel_prefs');
    if (saved) {
      const prefs = JSON.parse(saved);
      if (prefs.position) position.value = prefs.position;
      if (prefs.isCompact !== undefined) isCompact.value = prefs.isCompact;
    }
  } catch (e) {
    // ignore
  }
};

const savePanelPreferences = () => {
  try {
    localStorage.setItem('wa_sip_dialer_panel_prefs', JSON.stringify({
      position: position.value,
      isCompact: isCompact.value,
    }));
  } catch (e) {
    // ignore
  }
};

const toggleCompact = () => {
  isCompact.value = !isCompact.value;
  savePanelPreferences();
  nextTick(() => constrainToViewport());
};

// Drag functionality
let dragStartX = 0;
let dragStartY = 0;
let initialX = 0;
let initialY = 0;

const startDrag = (e) => {
  if (e.target.closest('button') || e.target.closest('input')) return;
  isDragging.value = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  if (position.value.x === null) {
    const rect = panelRef.value?.getBoundingClientRect();
    if (rect) { initialX = rect.left; initialY = rect.top; }
  } else {
    initialX = position.value.x;
    initialY = position.value.y;
  }
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  e.preventDefault();
};

const onDrag = (e) => {
  if (!isDragging.value) return;
  const deltaX = e.clientX - dragStartX;
  const deltaY = e.clientY - dragStartY;
  let newX = initialX + deltaX;
  let newY = initialY + deltaY;
  const panelHeight = isCompact.value ? PANEL_COMPACT_HEIGHT : PANEL_EXPANDED_HEIGHT;
  const maxX = window.innerWidth - PANEL_WIDTH - VIEWPORT_PADDING;
  const maxY = window.innerHeight - panelHeight - VIEWPORT_PADDING;
  newX = Math.max(VIEWPORT_PADDING, Math.min(newX, maxX));
  newY = Math.max(VIEWPORT_PADDING, Math.min(newY, maxY));
  position.value = { x: newX, y: newY };
};

const stopDrag = () => {
  if (isDragging.value) {
    isDragging.value = false;
    savePanelPreferences();
  }
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

const constrainToViewport = () => {
  if (position.value.x === null) return;
  const panelHeight = isCompact.value ? PANEL_COMPACT_HEIGHT : PANEL_EXPANDED_HEIGHT;
  const maxX = window.innerWidth - PANEL_WIDTH - VIEWPORT_PADDING;
  const maxY = window.innerHeight - panelHeight - VIEWPORT_PADDING;
  let needsUpdate = false;
  let newX = position.value.x;
  let newY = position.value.y;
  if (newX > maxX) { newX = Math.max(VIEWPORT_PADDING, maxX); needsUpdate = true; }
  if (newY > maxY) { newY = Math.max(VIEWPORT_PADDING, maxY); needsUpdate = true; }
  if (newX < VIEWPORT_PADDING) { newX = VIEWPORT_PADDING; needsUpdate = true; }
  if (newY < VIEWPORT_PADDING) { newY = VIEWPORT_PADDING; needsUpdate = true; }
  if (needsUpdate) position.value = { x: newX, y: newY };
};

const handleWindowResize = () => constrainToViewport();

const panelStyle = computed(() => {
  if (position.value.x === null) return {};
  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
    right: 'auto',
    bottom: 'auto',
  };
});

// ============================================
// Dialpad
// ============================================
const phoneNumber = ref(props.initialNumber || '');
const callDuration = ref(0);
let durationTimer = null;
let callCooldownTimer = null;

const isCallCooldown = ref(false);
const isMakingCall = ref(false);
const isTogglingMute = ref(false);
const isTogglingHold = ref(false);
const isTransferring = ref(false);
const isHangingUp = ref(false);
const isAnswering = ref(false);
const isRejecting = ref(false);

// Shared WhatsApp call-permission flow (check → request → wait → dial).
// state: idle | checking | requesting | waiting | permitted | denied
const { state: permissionState, requestCall: requestCallPermission } =
  useWhatsAppCallPermission();

// Transfer modal
const showTransferModal = ref(false);
const transferTarget = ref('');
const transferMode = ref('blind'); // 'blind' | 'attended'
const isCompletingTransfer = ref(false);
const isCancellingConsultation = ref(false);

const dialPadKeys = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

// Duration timer control
watch(callState, (newState, oldState) => {
  if (newState === 'connected') {
    startDurationTimer();
  } else if (newState === 'ended' || newState === 'idle') {
    if (oldState === 'connected' || oldState === 'calling' || oldState === 'ringing' || oldState === 'connecting') {
      addToHistory({
        number: callNumber.value || phoneNumber.value,
        duration: callDuration.value,
        type: callDirection.value === 'inbound' ? 'incoming' : 'outgoing',
        name: selectedContact.value?.name || remoteDisplayName.value || null,
        thumbnail: selectedContact.value?.thumbnail || null,
      });
    }
    stopDurationTimer();
    isTransferReceived.value = false;
    if (oldState === 'calling' || oldState === 'ringing' || oldState === 'connected' || oldState === 'connecting') {
      phoneNumber.value = '';
      selectedContact.value = null;
      isCallCooldown.value = true;
      clearTimeout(callCooldownTimer);
      callCooldownTimer = setTimeout(() => {
        isCallCooldown.value = false;
      }, POST_CALL_COOLDOWN_MS);
    }
  }
});

function startDurationTimer() {
  stopDurationTimer();
  callDuration.value = 0;
  durationTimer = setInterval(() => {
    callDuration.value = getCallDuration();
  }, 1000);
}

function stopDurationTimer() {
  if (durationTimer) {
    clearInterval(durationTimer);
    durationTimer = null;
  }
}

const displayNumber = computed(() => {
  if (hasActiveCall.value && callNumber.value) return callNumber.value;
  if (selectedContact.value) {
    return selectedContact.value.phoneNumber || selectedContact.value.phone_number || phoneNumber.value;
  }
  return phoneNumber.value;
});

const canCall = computed(() => {
  const num = displayNumber.value;
  return !!(isConnected.value && !hasActiveCall.value && num && String(num).length >= 1);
});

const consultationDisplayName = computed(() => {
  const target = consultationTarget.value || (isConsultationActive.value ? transferTarget.value : null);
  if (!target) return null;
  const agent = findAgentByExtension(target);
  return agent ? agent.name : target;
});

const statusText = computed(() => {
  if (isConnecting.value) return t('WHATSAPP_SIP_DIALER.STATUS.CONNECTING');
  if (connectionError.value) return t('WHATSAPP_SIP_DIALER.STATUS.CONNECTION_ERROR');
  if (!isConnected.value) return t('WHATSAPP_SIP_DIALER.STATUS.DISCONNECTED');
  switch (callState.value) {
    case 'calling': return t('WHATSAPP_SIP_DIALER.STATUS.CALLING');
    case 'ringing': return callDirection.value === 'incoming' ? t('WHATSAPP_SIP_DIALER.STATUS.INCOMING_CALL') : t('WHATSAPP_SIP_DIALER.STATUS.RINGING');
    case 'connecting': return t('WHATSAPP_SIP_DIALER.STATUS.CONNECTING');
    case 'connected': return formatDuration(callDuration.value);
    case 'ended': return t('WHATSAPP_SIP_DIALER.STATUS.CALL_ENDED');
    default: return t('WHATSAPP_SIP_DIALER.STATUS.READY');
  }
});

const statusColor = computed(() => {
  if (connectionError.value || !isConnected.value) return 'text-n-ruby-11';
  if (isConnecting.value) return 'text-n-amber-11';
  if (isInCall.value) return 'text-green-600 dark:text-green-400';
  return 'text-n-slate-11';
});

const pressKey = digit => {
  if (hasActiveCall.value) {
    sendDTMF(digit);
  } else {
    phoneNumber.value += digit;
    if (selectedContact.value) selectedContact.value = null;
  }
};

const deleteLastDigit = () => {
  if (!hasActiveCall.value) phoneNumber.value = phoneNumber.value.slice(0, -1);
};

const clearNumber = () => {
  phoneNumber.value = '';
  selectedContact.value = null;
  searchQuery.value = '';
};

const callFromHistory = item => {
  phoneNumber.value = item.number;
  if (item.name) {
    selectedContact.value = { name: item.name, thumbnail: item.thumbnail };
  }
  activeTab.value = 'dialpad';
};

// Normalize number to E.164 for SIP trunk (+55 prefix for Brazilian numbers)
const normalizeForSip = num => {
  const digits = num.replace(/\D/g, '');
  // Already has +55 prefix
  if (num.startsWith('+55')) return num;
  // Already has 55 prefix (12-13 digits)
  if (digits.startsWith('55') && digits.length >= 12) return `+${digits}`;
  // Domestic BR: 10 digits (landline) or 11 digits (mobile)
  if (digits.length === 10 || digits.length === 11) return `+55${digits}`;
  // International or already complete — add + if missing
  if (digits.length >= 12) return num.startsWith('+') ? num : `+${digits}`;
  return num;
};

const handleMakeCall = async () => {
  if (!canCall.value || isCallCooldown.value || isMakingCall.value) return;

  // With 2+ trunks the backend requires an explicit outbound number (X-Inbox-Id),
  // or the call is rejected. Block dialing until a trunk is selected.
  if (hasMultipleTrunks.value && !selectedInboxId.value) {
    toast.error(t('WHATSAPP_SIP_DIALER.TRUNK.SELECT_REQUIRED'));
    return;
  }

  const inboxId = selectedInboxId.value;
  isMakingCall.value = true;

  try {
    const sipNumber = normalizeForSip(displayNumber.value);
    const waId = sipNumber.replace(/\D/g, '');

    const dial = () => {
      try {
        sipMakeCall(sipNumber, event => {
          if (event.type === 'failed') {
            toast.error(t('WHATSAPP_SIP_DIALER.TOAST.CALL_FAILED'));
          }
        }, inboxId);
      } catch (err) {
        console.error('[WhatsAppSipDialerPanel] Error making call:', err);
        toast.error(err.message);
      }
    };

    // Auto-call (e.g. launched from the conversation button after permission was
    // already granted there) dials directly. Ramal interno (SIP) também disca
    // direto — não é número WhatsApp, não há permissão a pedir. Demais números
    // passam pelo fluxo de permissão (check → request → wait → dial).
    if (props.autoCall || isInternalExtension(displayNumber.value)) {
      dial();
    } else {
      await requestCallPermission({ waId, inboxId, onPermitted: dial });
    }
  } finally {
    isMakingCall.value = false;
  }
};

const handleHangup = async () => {
  if (isHangingUp.value) return;
  isHangingUp.value = true;
  try {
    hangup();
  } finally {
    setTimeout(() => { isHangingUp.value = false; }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleAnswerCall = async () => {
  if (isAnswering.value) return;
  isAnswering.value = true;
  try {
    sipAnswerCall();
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Error answering call:', err);
  } finally {
    setTimeout(() => { isAnswering.value = false; }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleRejectCall = async () => {
  if (isRejecting.value) return;
  isRejecting.value = true;
  try {
    hangup();
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Error rejecting call:', err);
  } finally {
    setTimeout(() => { isRejecting.value = false; }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleToggleMute = async () => {
  if (isTogglingMute.value) return;
  isTogglingMute.value = true;
  try {
    toggleMute();
  } finally {
    setTimeout(() => { isTogglingMute.value = false; }, SHORT_GUARD_MS);
  }
};

const handleToggleHold = async () => {
  if (isTogglingHold.value) return;
  isTogglingHold.value = true;
  try {
    const wasOnHold = isOnHold.value;
    sipToggleHold();
    toast.info(
      wasOnHold
        ? t('WHATSAPP_SIP_DIALER.TOAST.RESUMED')
        : t('WHATSAPP_SIP_DIALER.TOAST.ON_HOLD')
    );
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Hold error:', err);
    toast.error(err.message);
  } finally {
    setTimeout(() => { isTogglingHold.value = false; }, SHORT_GUARD_MS);
  }
};

const handleTransfer = () => {
  transferTarget.value = '';
  transferMode.value = 'blind';
  showTransferModal.value = true;
};

const confirmTransfer = async () => {
  if (!transferTarget.value || isTransferring.value) return;
  isTransferring.value = true;

  const targetExtension = transferTarget.value;
  const originalCallerNumber = callNumber.value;
  const originalCallerName = callerContact.value?.name || callerAgent.value?.name || null;
  const transferorExtension = currentUserExtension.value;
  const transferorName = currentUser.value?.name || null;
  const targetAgent = findAgentByExtension(targetExtension);
  const targetDisplayName = targetAgent?.name || targetExtension;

  try {
    if (transferMode.value === 'blind') {
      sipTransfer(targetExtension);
      toast.info(
        t('WHATSAPP_SIP_DIALER.TOAST.TRANSFERRING', { name: targetDisplayName })
      );
      showTransferModal.value = false;
      transferTarget.value = '';
      transferMode.value = 'blind';
      if (targetExtension) {
        try {
          await TransferNotificationsAPI.create({
            target_extension: targetExtension,
            original_caller_number: originalCallerNumber,
            original_caller_name: originalCallerName,
            transferor_extension: transferorExtension,
            transferor_name: transferorName,
          });
        } catch (notifyErr) {
          // Non-blocking
        }
      }
    } else {
      sipAttendedTransfer(targetExtension);
      // Modal stays open and switches to consultation phase
    }
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Transfer error:', err);
    toast.error(err.message);
  } finally {
    setTimeout(() => { isTransferring.value = false; }, SHORT_GUARD_MS);
  }
};

const handleCompleteAttendedTransfer = async () => {
  if (isCompletingTransfer.value) return;
  isCompletingTransfer.value = true;

  const targetExtension = transferTarget.value;
  const originalCallerNumber = callNumber.value;
  const originalCallerName = callerContact.value?.name || callerAgent.value?.name || null;
  const transferorExtension = currentUserExtension.value;
  const transferorName = currentUser.value?.name || null;
  const targetAgent = findAgentByExtension(targetExtension);
  const targetDisplayName = targetAgent?.name || targetExtension;

  try {
    sipCompleteAttendedTransfer();
    toast.success(
      t('WHATSAPP_SIP_DIALER.TOAST.TRANSFER_DONE', { name: targetDisplayName })
    );
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';
    if (targetExtension) {
      try {
        await TransferNotificationsAPI.create({
          target_extension: targetExtension,
          original_caller_number: originalCallerNumber,
          original_caller_name: originalCallerName,
          transferor_extension: transferorExtension,
          transferor_name: transferorName,
        });
      } catch (notifyErr) {
        // Non-blocking
      }
    }
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Complete attended transfer error:', err);
    toast.error(err.message);
  } finally {
    setTimeout(() => { isCompletingTransfer.value = false; }, DOUBLE_CLICK_GUARD_MS);
  }
};

const handleCancelConsultation = async () => {
  if (isCancellingConsultation.value) return;
  isCancellingConsultation.value = true;
  try {
    sipCancelAttendedTransfer();
    toast.info(t('WHATSAPP_SIP_DIALER.TOAST.CONSULTATION_CANCELLED'));
    showTransferModal.value = false;
    transferTarget.value = '';
    transferMode.value = 'blind';
  } catch (err) {
    console.error('[WhatsAppSipDialerPanel] Cancel consultation error:', err);
    toast.error(err.message);
  } finally {
    setTimeout(() => { isCancellingConsultation.value = false; }, DOUBLE_CLICK_GUARD_MS);
  }
};

// Handler when a transfer is completed to this agent
const handleTransferCompleted = (data) => {
  const myExtension = currentUserExtension.value;
  if (!myExtension || String(data.target_extension) !== String(myExtension)) return;

  isTransferReceived.value = true;

  if (data.original_caller_number) {
    callerAgent.value = null;
    if (data.original_caller_name && data.original_caller_name !== data.original_caller_number) {
      callerContact.value = {
        name: data.original_caller_name,
        phone_number: data.original_caller_number,
      };
    } else {
      callerContact.value = null;
    }
    lookupCaller(data.original_caller_number);
  }
};

onMounted(async () => {
  loadPanelPreferences();
  constrainToViewport();

  if (hasActiveCall.value) {
    activeTab.value = 'dialpad';
    isCompact.value = false;
  }

  window.addEventListener('resize', handleWindowResize);
  emitter.on(BUS_EVENTS.TELEPHONY_TRANSFER_COMPLETED, handleTransferCompleted);

  // Connection is handled by WhatsAppSipDialerLauncher on mount
  loadInitialContacts();
  loadExtensions();
  store.dispatch('agents/get');
  // Inboxes back the trunk's channel icon (ChannelIcon needs the inbox object).
  store.dispatch('inboxes/get').catch(() => {});

  if (trunks.value.length === 0) {
    store.dispatch('whatsappSipTrunks/fetch').catch(() => {});
  }

  // Auto-call: wait until canCall is true then dial
  if (props.autoCall && props.initialNumber) {
    if (canCall.value) {
      handleMakeCall();
    } else {
      const stopWatch = watch(canCall, ready => {
        if (ready) {
          stopWatch();
          handleMakeCall();
        }
      });
    }
  }
});

onUnmounted(() => {
  stopDurationTimer();
  clearTimeout(searchTimeout);
  clearTimeout(callCooldownTimer);
  window.removeEventListener('resize', handleWindowResize);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  emitter.off(BUS_EVENTS.TELEPHONY_TRANSFER_COMPLETED, handleTransferCompleted);
});
</script>

<template>
  <div
    ref="panelRef"
    class="fixed z-50"
    :class="position.x === null ? 'bottom-4 ltr:right-4 rtl:left-4' : ''"
    :style="panelStyle"
    @click.capture="handleUserInteraction"
  >
    <div
      class="bg-n-solid-2 rounded-2xl shadow-2xl overflow-hidden border border-n-weak flex flex-col relative w-[280px]"
      :class="[
        { 'select-none': isDragging },
        // Altura fixa quando expandido — DEVE casar com PANEL_EXPANDED_HEIGHT
        // (530) usado no cálculo de drag. Antes era 'h-[485fapx]' (typo, valor
        // CSS inválido → altura virava automática e encolhia conforme a aba).
        isCompact ? '' : 'h-[530px]'
      ]"
    >
      <!-- Header (Draggable) -->
      <div
        class="flex items-center justify-between px-3 py-2 border-b cursor-move"
        :class="{
          'cursor-grabbing': isDragging,
          'border-b-0': isCompact && !hasActiveCall,
          'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800': hasActiveCall && (callState === 'calling' || callState === 'ringing'),
          'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-700': hasActiveCall && (callState === 'connecting' || callState === 'connected'),
          'bg-n-solid-3 border-n-weak': !hasActiveCall,
        }"
        @mousedown="startDrag"
      >
        <!-- Active call: show call info in header -->
        <div v-if="hasActiveCall" class="flex items-center gap-2 flex-1 min-w-0">
          <span
            class="size-2 rounded-full animate-pulse flex-shrink-0"
            :class="{
              'bg-green-500': callState === 'calling' || callState === 'ringing',
              'bg-green-600': callState === 'connecting' || callState === 'connected',
            }"
          />
          <p
            class="text-sm truncate"
            :class="{
              'text-green-700 dark:text-green-400': callState === 'calling' || callState === 'ringing',
              'text-green-800 dark:text-green-300': callState === 'connecting' || callState === 'connected',
            }"
          >
            <span class="font-semibold">{{ statusText }}</span>
            <span v-if="callerAgent || callerContact || callNumber || remoteDisplayName" class="font-normal"> · {{ callerAgent?.name || callerContact?.name || remoteDisplayName || callNumber }}</span>
          </p>
        </div>

        <!-- Normal header -->
        <div v-else class="flex items-center gap-2 flex-1">
          <span class="i-lucide-message-circle size-4 text-green-600 dark:text-green-400" />
          <span class="text-sm font-medium text-n-slate-12">{{ t('WHATSAPP_SIP_DIALER.TITLE') }}</span>
        </div>

        <div class="flex items-center gap-1">
          <!-- Compact/Expand Button (only show during calls) -->
          <Button
            v-if="hasActiveCall"
            :icon="isCompact ? 'i-lucide-maximize-2' : 'i-lucide-minimize-2'"
            variant="ghost"
            color="slate"
            size="xs"
            v-tooltip="isCompact ? t('WHATSAPP_SIP_DIALER.ACTIONS.EXPAND') : t('WHATSAPP_SIP_DIALER.ACTIONS.MINIMIZE')"
            @click.stop="toggleCompact"
          />
          <!-- Close Button -->
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="xs"
            @click.stop="emit('close')"
          />
        </div>
      </div>

      <!-- Content (hidden when compact) -->
      <template v-if="!isCompact">

        <!-- Status bar -->
        <div class="px-3 py-1.5 border-b border-n-weak flex items-center gap-2 bg-n-solid-1 flex-shrink-0">
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="{
              'bg-green-500': isConnected && !hasActiveCall,
              'bg-n-amber-9 animate-pulse': isConnecting,
              'bg-n-ruby-9': !isConnected && !isConnecting,
              'bg-green-600 animate-pulse': hasActiveCall,
            }"
          />
          <span class="text-xs truncate" :class="statusColor">
            {{ statusText }}
          </span>
          <span v-if="connectionError" class="text-xs text-n-ruby-11 truncate ml-auto">
            {{ connectionError }}
          </span>
          <!-- SIP On/Off Toggle -->
          <button
            class="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors"
            :class="isConnected || isConnecting
              ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
              : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3 hover:text-n-slate-12'"
            :disabled="isTogglingConnection || hasActiveCall"
            v-tooltip="hasActiveCall ? t('WHATSAPP_SIP_DIALER.TOGGLE.CANNOT_DISCONNECT_DURING_CALL') : (isConnected ? t('WHATSAPP_SIP_DIALER.TOGGLE.DISCONNECT') : t('WHATSAPP_SIP_DIALER.TOGGLE.CONNECT'))"
            @click="toggleSipConnection"
          >
            <span v-if="isTogglingConnection" class="i-lucide-loader-2 size-3 animate-spin" />
            <template v-else>
              <span class="size-3" :class="isConnected || isConnecting ? 'i-lucide-power' : 'i-lucide-power-off'" />
              {{ isConnected || isConnecting ? 'ON' : 'OFF' }}
            </template>
          </button>
        </div>

        <!-- Tabs -->
        <div class="flex items-center gap-1 px-2 py-1.5 border-b border-n-weak flex-shrink-0">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            v-tooltip="tab.label"
            class="flex-1 flex items-center justify-center py-1.5 rounded-md transition-all duration-200"
            :class="activeTab === tab.id
              ? ['bg-n-alpha-2 dark:bg-n-solid-3 shadow-sm', tab.activeColor]
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'"
            @click="activeTab = tab.id"
          >
            <span :class="[tab.icon, 'size-4']" />
          </button>
        </div>

        <DialerPauseBar :is-connected="isConnected" />

        <div class="flex-1 overflow-hidden flex flex-col min-h-0">

          <!-- Dialpad Tab -->
          <div v-if="activeTab === 'dialpad'" class="flex-1 flex flex-col min-h-0">

            <!-- Incoming Call UI -->
            <div
              v-if="isIncomingCall"
              class="flex-1 flex flex-col items-center justify-center px-4 py-6 bg-gradient-to-b from-n-solid-3 to-n-solid-2"
            >
              <div class="flex flex-col items-center gap-4 mb-8">
                <div class="relative">
                  <div
                    v-if="callerAgent || callerContact"
                    class="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-pulse overflow-hidden"
                  >
                    <Avatar
                      :src="callerThumbnail"
                      :name="callerDisplayName"
                      :size="80"
                      rounded-full
                    />
                  </div>
                  <div v-else class="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-pulse">
                    <span class="i-lucide-phone-incoming size-10 text-green-600 dark:text-green-400" />
                  </div>
                  <div class="absolute -inset-2 rounded-full border-2 border-green-400 dark:border-green-600 animate-ping opacity-50" />
                </div>
                <div class="text-center">
                  <p class="text-sm text-n-slate-10 mb-1">{{ t('WHATSAPP_SIP_DIALER.INCOMING.LABEL') }}</p>
                  <p class="text-xl font-semibold text-n-slate-12">{{ callerDisplayName }}</p>
                  <p v-if="callerAgent || callerContact" class="text-sm text-n-slate-10 mt-0.5">
                    {{ callerAgent ? `Ramal ${callNumber}` : callNumber }}
                  </p>
                  <p v-else-if="callNumber && callNumber !== callerDisplayName" class="text-sm text-n-slate-10 mt-0.5">
                    {{ callNumber }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-6">
                <button
                  class="flex flex-col items-center gap-2 group"
                  :disabled="isRejecting"
                  :class="{ 'opacity-50 pointer-events-none': isRejecting }"
                  @click="handleRejectCall"
                >
                  <div class="w-16 h-16 rounded-full bg-n-ruby-9 hover:bg-n-ruby-10 flex items-center justify-center transition-colors shadow-lg">
                    <span v-if="!isRejecting" class="i-lucide-phone-off size-7 text-white" />
                    <span v-else class="i-lucide-loader-2 size-7 text-white animate-spin" />
                  </div>
                  <span class="text-xs text-n-slate-11 group-hover:text-n-slate-12">{{ t('WHATSAPP_SIP_DIALER.ACTIONS.DECLINE') }}</span>
                </button>
                <button
                  class="flex flex-col items-center gap-2 group"
                  :disabled="isAnswering"
                  :class="{ 'opacity-50 pointer-events-none': isAnswering }"
                  @click="handleAnswerCall"
                >
                  <div
                    class="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center transition-colors shadow-lg"
                    :class="{ 'animate-bounce': !isAnswering }"
                  >
                    <span v-if="!isAnswering" class="i-lucide-phone size-7 text-white" />
                    <span v-else class="i-lucide-loader-2 size-7 text-white animate-spin" />
                  </div>
                  <span class="text-xs text-n-slate-11 group-hover:text-n-slate-12">{{ t('WHATSAPP_SIP_DIALER.ACTIONS.ANSWER') }}</span>
                </button>
              </div>
            </div>

            <!-- Normal Dialpad UI (when not incoming call) -->
            <template v-else>

              <!-- Miolo rolável: o painel tem altura FIXA (530px, pra não
                   redimensionar ao trocar de aba) e as seções somadas podem
                   passar disso quando a linha de pausa está ativa junto com
                   uma chamada. Quem cede é o miolo (trunk/número/teclado, com
                   scroll) — a fileira de botões de ligar/desligar fica pinada
                   embaixo e NUNCA é cortada pelo overflow-hidden do painel. -->
              <div class="flex-1 min-h-0 overflow-y-auto flex flex-col">

              <!-- Outbound trunk selector (which WhatsApp number to call from) -->
              <div
                v-if="trunks.length > 0 && !hasActiveCall"
                class="px-2 pt-2 flex-shrink-0 flex items-center justify-center"
              >
                <!-- Single trunk: fixed label -->
                <div
                  v-if="!hasMultipleTrunks"
                  class="flex items-center gap-2 w-full max-w-[240px] px-3 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak"
                >
                  <ChannelIcon
                    v-tooltip.top="inboxFor(selectedTrunk).name"
                    :inbox="inboxFor(selectedTrunk)"
                    class="size-3.5 text-n-slate-10 flex-shrink-0"
                  />
                  <span class="text-[11px] text-n-slate-11 truncate">
                    {{ selectedTrunk?.phone_number }}
                  </span>
                </div>

                <!-- Multiple trunks: dropdown -->
                <div v-else class="relative w-full max-w-[240px]">
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg bg-n-alpha-2 dark:bg-n-solid-1 border border-n-weak hover:bg-n-alpha-3 transition-colors"
                    @click="isTrunkMenuOpen = !isTrunkMenuOpen"
                  >
                    <ChannelIcon
                      v-if="selectedTrunk"
                      v-tooltip.top="inboxFor(selectedTrunk).name"
                      :inbox="inboxFor(selectedTrunk)"
                      class="size-3.5 text-n-slate-10 flex-shrink-0"
                    />
                    <span
                      v-else
                      class="i-lucide-phone-outgoing size-3.5 text-n-slate-10 flex-shrink-0"
                    />
                    <span class="flex-1 text-[11px] text-n-slate-12 truncate text-left">
                      {{ selectedTrunk?.phone_number || t('WHATSAPP_SIP_DIALER.TRUNK.PLACEHOLDER') }}
                    </span>
                    <span
                      class="i-lucide-chevron-down size-3.5 text-n-slate-10 flex-shrink-0 transition-transform"
                      :class="{ 'rotate-180': isTrunkMenuOpen }"
                    />
                  </button>

                  <template v-if="isTrunkMenuOpen">
                    <div
                      class="fixed inset-0 z-10"
                      @click="isTrunkMenuOpen = false"
                    />
                    <div
                      class="absolute z-20 left-0 right-0 mt-1 py-1 rounded-lg bg-n-solid-2 border border-n-weak shadow-lg max-h-40 overflow-y-auto"
                    >
                      <button
                        v-for="trunk in trunks"
                        :key="trunk.id"
                        type="button"
                        class="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-n-alpha-2 transition-colors"
                        :class="trunk.inbox_id === selectedInboxId ? 'text-n-slate-12' : 'text-n-slate-11'"
                        @click="selectTrunk(trunk.inbox_id)"
                      >
                        <span
                          class="i-lucide-check size-3.5 flex-shrink-0"
                          :class="trunk.inbox_id === selectedInboxId ? 'text-n-teal-10' : 'opacity-0'"
                        />
                        <ChannelIcon
                          v-tooltip.top="inboxFor(trunk).name"
                          :inbox="inboxFor(trunk)"
                          class="size-3.5 text-n-slate-10 flex-shrink-0"
                        />
                        <span class="text-[11px] truncate">{{ trunk.phone_number }}</span>
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Number Display -->
              <div class="px-2 py-2 flex-shrink-0 flex items-center justify-center">
                <div class="flex items-center gap-2 w-full max-w-[240px] bg-n-alpha-2 dark:bg-n-solid-1 rounded-lg px-3 py-2 border border-n-weak">
                  <div class="flex-1 min-w-0">
                    <p
                      v-if="selectedContact && !hasActiveCall"
                      class="text-[10px] text-n-slate-10 truncate text-center"
                    >
                      {{ selectedContact.name }}
                    </p>
                    <input
                      v-model="phoneNumber"
                      type="tel"
                      :placeholder="t('WHATSAPP_SIP_DIALER.INPUT.PLACEHOLDER')"
                      class="w-full text-xl font-semibold text-n-slate-12 bg-transparent border-none focus:outline-none text-center no-margin"
                      :readonly="hasActiveCall"
                    />
                  </div>
                  <button
                    v-if="phoneNumber && !hasActiveCall"
                    class="p-1 rounded hover:bg-n-alpha-2 transition-colors"
                    @click="deleteLastDigit"
                    @mousedown.prevent
                  >
                    <span class="i-lucide-delete size-5 text-n-slate-10 hover:text-n-slate-12" />
                  </button>
                </div>
              </div>

              <!-- Dial Pad -->
              <div
                class="px-2 flex-shrink-0 flex items-center justify-center"
                :class="hasActiveCall ? 'py-2' : 'py-3'"
              >
                <div class="grid grid-cols-3 gap-2 w-full max-w-[240px]">
                  <button
                    v-for="key in dialPadKeys"
                    :key="key.digit"
                    class="flex flex-col items-center justify-center h-11 rounded-lg bg-n-solid-3 hover:bg-n-alpha-2 active:bg-n-alpha-3 transition-colors"
                    @click="pressKey(key.digit)"
                  >
                    <span class="text-lg font-medium text-n-slate-12">{{ key.digit }}</span>
                    <span v-if="key.letters" class="text-[9px] text-n-slate-10 tracking-wider">
                      {{ key.letters }}
                    </span>
                  </button>
                </div>
              </div>

              </div>

              <!-- Action Buttons (not in call) -->
              <div v-if="!hasActiveCall" class="px-2 py-1.5 border-t border-n-weak flex-shrink-0 flex justify-center">
                <div class="flex items-center gap-1.5 w-full max-w-[240px]">
                  <Button
                    v-if="phoneNumber"
                    icon="i-lucide-x"
                    variant="ghost"
                    color="slate"
                    class="flex-shrink-0"
                    @click="clearNumber"
                  />
                  <button
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                    :class="canCall && !isMakingCall && permissionState !== 'waiting'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-n-solid-3 text-n-slate-10 cursor-not-allowed'"
                    :disabled="!canCall || isCallCooldown || isMakingCall || permissionState === 'waiting'"
                    @click="handleMakeCall"
                  >
                    <span v-if="isMakingCall || permissionState === 'waiting' || isConnecting" class="i-lucide-loader-2 size-4 animate-spin" />
                    <span v-else class="i-lucide-phone size-4" />
                    {{ permissionState === 'waiting' ? t('WHATSAPP_SIP_CALL.PERMISSION.WAITING') : t('WHATSAPP_SIP_DIALER.ACTIONS.CALL') }}
                  </button>
                </div>
              </div>

              <!-- In-Call Controls -->
              <div v-if="hasActiveCall && !isIncomingCall" class="px-2 py-2 border-t border-n-weak flex-shrink-0 flex justify-center">
                <div class="flex items-center justify-center gap-3 w-full max-w-[240px]">
                  <!-- Mute -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isTogglingMute"
                    :class="{ 'opacity-50 pointer-events-none': isTogglingMute }"
                    @click="handleToggleMute"
                  >
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      :class="isMuted ? 'bg-n-ruby-9 hover:bg-n-ruby-10' : 'bg-n-solid-3 hover:bg-n-alpha-2'"
                    >
                      <span
                        v-if="!isTogglingMute"
                        :class="isMuted ? 'i-lucide-mic-off text-white' : 'i-lucide-mic text-n-slate-11'"
                        class="size-5"
                      />
                      <span v-else class="i-lucide-loader-2 size-5 text-n-slate-11 animate-spin" />
                    </div>
                    <span class="text-[10px] text-n-slate-10">{{ isMuted ? 'Ativar' : 'Mudo' }}</span>
                  </button>

                  <!-- Hold -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isTogglingHold"
                    :class="{ 'opacity-50 pointer-events-none': isTogglingHold }"
                    @click="handleToggleHold"
                  >
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                      :class="isOnHold ? 'bg-n-amber-9 hover:bg-n-amber-10' : 'bg-n-solid-3 hover:bg-n-alpha-2'"
                    >
                      <span
                        v-if="!isTogglingHold"
                        :class="isOnHold ? 'i-lucide-play text-white' : 'i-lucide-pause text-n-slate-11'"
                        class="size-5"
                      />
                      <span v-else class="i-lucide-loader-2 size-5 text-n-slate-11 animate-spin" />
                    </div>
                    <span class="text-[10px] text-n-slate-10">{{ isOnHold ? 'Retomar' : 'Espera' }}</span>
                  </button>

                  <!-- Transfer (hidden during internal consultation call, shown after transfer received) -->
                  <button
                    v-if="canShowTransferButton"
                    class="flex flex-col items-center gap-1 group"
                    @click="handleTransfer"
                  >
                    <div class="w-10 h-10 rounded-full bg-n-solid-3 hover:bg-n-alpha-2 flex items-center justify-center transition-colors">
                      <span class="i-lucide-phone-forwarded size-5 text-n-slate-11" />
                    </div>
                    <span class="text-[10px] text-n-slate-10">Transferir</span>
                  </button>

                  <!-- Hangup -->
                  <button
                    class="flex flex-col items-center gap-1 group"
                    :disabled="isHangingUp"
                    :class="{ 'opacity-50 pointer-events-none': isHangingUp }"
                    @click="handleHangup"
                  >
                    <div class="w-10 h-10 rounded-full bg-n-ruby-9 hover:bg-n-ruby-10 flex items-center justify-center transition-colors">
                      <span v-if="!isHangingUp" class="i-lucide-phone-off size-5 text-white" />
                      <span v-else class="i-lucide-loader-2 size-5 text-white animate-spin" />
                    </div>
                    <span class="text-[10px] text-n-slate-10">{{ t('WHATSAPP_SIP_DIALER.ACTIONS.HANG_UP') }}</span>
                  </button>
                </div>
              </div>

            </template>
          </div>

          <!-- Extensions Tab -->
          <div v-else-if="activeTab === 'extensions'" class="flex-1 flex flex-col min-h-0">
            <div v-if="isLoadingExtensions && extensionsList.length === 0" class="flex items-center justify-center py-8">
              <Spinner size="small" />
            </div>
            <div v-else-if="extensionsList.length === 0" class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10">
              <span class="i-lucide-headphones size-12 mb-2 opacity-50" />
              <p class="text-sm">Nenhum ramal disponível</p>
            </div>
            <div v-else class="flex-1 flex flex-col min-h-0">
              <div class="px-3 py-2 border-b border-n-weak flex-shrink-0 space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-xs text-n-slate-10 font-medium">Ramais Disponíveis ({{ extensions.length }})</span>
                  <button
                    class="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                    :disabled="isLoadingExtensions"
                    @click="loadExtensions"
                  >
                    <span :class="['i-lucide-refresh-cw size-3', isLoadingExtensions ? 'animate-spin' : '']" />
                    Atualizar
                  </button>
                </div>
                <input
                  v-model="extensionSearch"
                  type="text"
                  placeholder="Buscar por nome ou ramal..."
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div v-if="extensions.length > 0" class="flex-1 overflow-y-auto">
                <div class="p-2">
                  <button
                    v-for="ext in extensions"
                    :key="ext.id || ext.extension_number"
                    v-tooltip="!isConnected ? t('WHATSAPP_SIP_DIALER.STATUS.DISCONNECTED') : ''"
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left"
                    :class="isConnected ? 'hover:bg-n-alpha-1 cursor-pointer' : 'opacity-50 cursor-not-allowed'"
                    :disabled="!isConnected"
                    @click="isConnected && callExtension(ext)"
                  >
                    <div class="flex items-center justify-center w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/20">
                      <span class="i-lucide-headphones size-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium truncate text-n-slate-12">
                        {{ ext.display_name || 'Ramal ' + ext.extension_number }}
                      </p>
                      <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
                        <span
                          v-if="extensionPresenceDot(ext)"
                          v-tooltip="extensionPresenceDot(ext).label"
                          class="inline-block size-1.5 rounded-full"
                          :class="extensionPresenceDot(ext).dotClass"
                        />
                        Ramal {{ ext.extension_number }}
                      </p>
                    </div>
                    <span class="i-lucide-phone size-4" :class="isConnected ? 'text-green-600 dark:text-green-400' : 'text-n-slate-10'" />
                  </button>
                </div>
              </div>
              <div v-else class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10">
                <span class="i-lucide-search size-8 mb-2 opacity-50" />
                <p class="text-sm">Nenhum ramal encontrado</p>
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div v-else-if="activeTab === 'history'" class="flex-1 flex flex-col min-h-0">
            <div v-if="callHistory.length === 0" class="flex-1 flex flex-col items-center justify-center py-8 text-n-slate-10">
              <span class="i-lucide-history size-12 mb-2 opacity-50" />
              <p class="text-sm">Nenhuma chamada recente</p>
            </div>
            <div v-else class="flex-1 flex flex-col min-h-0">
              <div class="flex items-center justify-between px-4 py-2 border-b border-n-weak flex-shrink-0">
                <span class="text-xs text-n-slate-10 font-medium">Chamadas Recentes</span>
                <button
                  class="text-xs text-n-ruby-11 hover:text-n-ruby-9"
                  @click="clearCallHistory"
                >
                  Limpar
                </button>
              </div>
              <div class="flex-1 overflow-y-auto">
                <div class="p-2">
                  <button
                    v-for="item in callHistory"
                    :key="item.id"
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-n-alpha-1 transition-colors text-left"
                    @click="callFromHistory(item)"
                  >
                    <div v-if="item.name" class="relative flex-shrink-0">
                      <Avatar
                        :src="item.thumbnail"
                        :name="item.name"
                        :size="36"
                        rounded-full
                      />
                      <span
                        class="absolute -bottom-0.5 -right-0.5 size-4 rounded-full bg-n-solid-2 flex items-center justify-center"
                      >
                        <span
                          :class="item.type === 'outgoing' ? 'i-lucide-phone-outgoing' : 'i-lucide-phone-incoming'"
                          class="size-2.5 text-n-slate-10"
                        />
                      </span>
                    </div>
                    <div v-else class="flex items-center justify-center w-9 h-9 rounded-full bg-n-solid-3 flex-shrink-0">
                      <span
                        :class="item.type === 'outgoing' ? 'i-lucide-phone-outgoing' : 'i-lucide-phone-incoming'"
                        class="size-4 text-n-slate-11"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-n-slate-12 truncate">
                        {{ item.name || item.number }}
                      </p>
                      <p class="text-xs text-n-slate-10 truncate">
                        <span v-if="item.name">{{ item.number }} · </span>
                        {{ formatHistoryTime(item.timestamp) }}
                        <span v-if="item.duration"> · {{ formatHistoryDuration(item.duration) }}</span>
                      </p>
                    </div>
                    <span class="i-lucide-phone size-4 text-n-teal-11" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div v-else-if="activeTab === 'contacts'" class="flex-1 flex flex-col min-h-0">
            <!-- Search -->
            <div class="p-3 border-b border-n-weak flex-shrink-0">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Buscar contatos..."
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <!-- Results -->
            <div
              ref="contactsListRef"
              class="flex-1 overflow-y-auto"
              @scroll="handleContactsScroll"
            >
              <div v-if="isSearching && contacts.length === 0" class="flex items-center justify-center py-8">
                <Spinner size="small" />
              </div>
              <div v-else-if="contacts.length === 0 && searchQuery.length >= 2" class="flex flex-col items-center justify-center py-8 text-n-slate-10">
                <span class="i-lucide-user-x size-12 mb-2 opacity-50" />
                <p class="text-sm">Nenhum contato encontrado</p>
              </div>
              <div v-else-if="contacts.length === 0 && !isSearching" class="flex flex-col items-center justify-center py-8 text-n-slate-10">
                <span class="i-lucide-users size-12 mb-2 opacity-50" />
                <p class="text-sm">Nenhum contato encontrado</p>
              </div>
              <div v-else class="p-2">
                <button
                  v-for="contact in contacts"
                  :key="contact.id"
                  class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-n-alpha-1 transition-colors text-left"
                  @click="selectContact(contact)"
                >
                  <Avatar
                    :src="contact.thumbnail"
                    :name="contact.name"
                    :size="36"
                    rounded-full
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ contact.name }}
                    </p>
                    <p class="text-xs text-n-slate-10 truncate">
                      {{ getContactPhone(contact) }}
                    </p>
                  </div>
                  <span class="i-lucide-phone size-4 text-green-600 dark:text-green-400" />
                </button>
                <!-- Loading more indicator -->
                <div v-if="isSearching && contacts.length > 0" class="flex items-center justify-center py-4">
                  <Spinner size="small" />
                </div>
              </div>
            </div>
          </div>

        </div>

      </template>

      <!-- Transfer Modal -->
      <div
        v-if="showTransferModal"
        class="absolute inset-0 bg-n-alpha-black2 flex flex-col"
      >
        <div class="bg-n-solid-2 flex flex-col h-full">

          <!-- PHASE 1: Select destination & mode (no consultation active) -->
          <template v-if="!isConsultationActive">
            <!-- Header -->
            <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0">
              <h4 class="text-sm font-semibold text-n-slate-12">Transferir Chamada</h4>
              <Button
                icon="i-lucide-x"
                variant="ghost"
                color="slate"
                size="xs"
                @click="showTransferModal = false; transferMode = 'blind';"
              />
            </div>

            <!-- Transfer mode toggle -->
            <div class="px-4 pt-3 pb-1 flex-shrink-0">
              <div class="flex rounded-lg border border-n-weak overflow-hidden">
                <button
                  class="flex-1 py-1.5 text-xs font-medium text-center transition-colors"
                  :class="transferMode === 'blind'
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-r border-n-weak'
                    : 'bg-n-solid-3 text-n-slate-10 hover:text-n-slate-12 border-r border-n-weak'"
                  @click="transferMode = 'blind'"
                >
                  Direta
                </button>
                <button
                  class="flex-1 py-1.5 text-xs font-medium text-center transition-colors"
                  :class="transferMode === 'attended'
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-n-solid-3 text-n-slate-10 hover:text-n-slate-12'"
                  @click="transferMode = 'attended'"
                >
                  Assistida
                </button>
              </div>
              <p class="text-[10px] text-n-slate-10 mt-1.5 px-0.5">
                {{ transferMode === 'blind'
                  ? 'Transfere diretamente sem consulta.'
                  : 'Consulta o destino antes de transferir.' }}
              </p>
            </div>

            <!-- Extensions list -->
            <div class="flex-1 overflow-y-auto min-h-0">
              <div v-if="extensions.length > 0" class="px-2 pb-2">
                <p class="px-2 py-1.5 text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">Ramais</p>
                <button
                  v-for="ext in extensions"
                  :key="ext.id || ext.extension_number"
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left"
                  :class="transferTarget === String(ext.extension_number) ? 'bg-green-100 dark:bg-green-900/20' : 'hover:bg-n-alpha-1'"
                  @click="transferTarget = String(ext.extension_number)"
                >
                  <div class="flex items-center justify-center w-8 h-8 rounded-full bg-n-solid-3">
                    <span class="i-lucide-headphones size-4 text-n-slate-11" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{ ext.display_name || 'Ramal ' + ext.extension_number }}
                    </p>
                    <p class="text-xs text-n-slate-10">{{ ext.extension_number }}</p>
                  </div>
                  <span
                    v-if="transferTarget === String(ext.extension_number)"
                    class="i-lucide-check size-4 text-green-600 dark:text-green-400"
                  />
                </button>
              </div>
              <div v-else class="flex flex-col items-center justify-center py-8 text-n-slate-10">
                <span class="i-lucide-headphones size-8 mb-2 opacity-50" />
                <p class="text-sm">Nenhum ramal disponível</p>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="px-4 py-3 border-t border-n-weak flex-shrink-0">
              <div class="flex gap-2">
                <Button
                  color="slate"
                  variant="ghost"
                  class="flex-1"
                  @click="showTransferModal = false; transferMode = 'blind';"
                >
                  Cancelar
                </Button>
                <button
                  class="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                  :class="transferTarget && !isTransferring
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-n-solid-3 text-n-slate-10 cursor-not-allowed'"
                  :disabled="!transferTarget || isTransferring"
                  @click="confirmTransfer"
                >
                  <span v-if="isTransferring" class="i-lucide-loader-2 size-4 animate-spin" />
                  {{ transferMode === 'blind' ? 'Transferir' : 'Consultar' }}
                </button>
              </div>
            </div>
          </template>

          <!-- PHASE 2: Consultation active -->
          <template v-else>
            <!-- Header (no close button during consultation) -->
            <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak flex-shrink-0">
              <h4 class="text-sm font-semibold text-n-slate-12">Consulta em Andamento</h4>
            </div>

            <!-- Consultation status -->
            <div class="flex-1 flex flex-col items-center justify-center px-4 py-6">
              <div class="flex flex-col items-center gap-4">
                <!-- Status indicator -->
                <div class="relative">
                  <div
                    class="w-16 h-16 rounded-full flex items-center justify-center"
                    :class="{
                      'bg-n-amber-3 dark:bg-n-amber-9/20': consultationState === 'calling' || consultationState === 'ringing',
                      'bg-green-100 dark:bg-green-900/20': consultationState === 'connected',
                    }"
                  >
                    <span
                      v-if="consultationState === 'calling' || consultationState === 'ringing'"
                      class="i-lucide-phone-outgoing size-8 text-n-amber-11 animate-pulse"
                    />
                    <span
                      v-else-if="consultationState === 'connected'"
                      class="i-lucide-phone-call size-8 text-green-600 dark:text-green-400"
                    />
                  </div>
                  <div
                    v-if="consultationState === 'calling' || consultationState === 'ringing'"
                    class="absolute -inset-2 rounded-full border-2 border-n-amber-6 animate-ping opacity-40"
                  />
                </div>

                <!-- Status text -->
                <div class="text-center">
                  <p class="text-sm text-n-slate-10 mb-1">
                    {{ consultationState === 'calling' || consultationState === 'ringing' ? 'Chamando...' :
                       consultationState === 'connected' ? 'Consulta conectada' : 'Consultando...' }}
                  </p>
                  <p class="text-lg font-semibold text-n-slate-12">
                    {{ consultationDisplayName || consultationTarget || transferTarget }}
                  </p>
                  <p v-if="consultationDisplayName" class="text-xs text-n-slate-10 mt-0.5">
                    Ramal {{ consultationTarget || transferTarget }}
                  </p>
                  <p class="text-xs text-n-slate-10 mt-1">
                    Chamada original em espera
                  </p>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="px-4 py-3 border-t border-n-weak flex-shrink-0">
              <div class="flex gap-2">
                <Button
                  color="slate"
                  variant="ghost"
                  class="flex-1"
                  :disabled="isCancellingConsultation"
                  :is-loading="isCancellingConsultation"
                  @click="handleCancelConsultation"
                >
                  Cancelar
                </Button>
                <button
                  class="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                  :class="consultationState === 'connected' && !isCompletingTransfer
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-n-solid-3 text-n-slate-10 cursor-not-allowed'"
                  :disabled="consultationState !== 'connected' || isCompletingTransfer"
                  @click="handleCompleteAttendedTransfer"
                >
                  <span v-if="isCompletingTransfer" class="i-lucide-loader-2 size-4 animate-spin" />
                  Transferir
                </button>
              </div>
            </div>
          </template>

        </div>
      </div>

    </div>
  </div>
</template>
